import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  IChatSessionData,
  IConversationHistory,
  IQuerySingleQuestion,
  ISingleConversationHistoryItem,
  TQueriesHistory,
} from "@/types/(my-space)/ai-assistant.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const aiAssistantApi = createApi({
  reducerPath: "aiAssistantApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: ["GetAllConversations", "GetAllQueries", "GetSingleConvesation", "GetAllQuestionsBySessionId", "AIShareLink"],
  endpoints: (builder) => ({
    aiSmartChat: builder.mutation<
      IApiResponse<IChatSessionData>,
      { message: string; sessionId?: string; isTemporary?: boolean }
    >({
      query: (body) => ({
        url: APIEndpoints.MySpace.AIAssistant.SmartChat,
        method: "POST",
        body,
      }),
    }),
    getSingleConversation: builder.query<
      IApiPaginatedResponse<IConversationHistory>,
      { sessionId: string; messageId?: number; page?: number; limit?: number; latest?: boolean; isTemporary?: boolean }
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.AIAssistant.GetSingleConversationHistory,
        params,
      }),
      providesTags: ["GetSingleConvesation"],
    }),
    getConversationHistory: builder.infiniteQuery<
      IApiPaginatedResponse<ISingleConversationHistoryItem[]>,
      { limit?: number; search: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { page, lastPage } = resp.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ pageParam, queryArg }) => ({
        url: APIEndpoints.MySpace.AIAssistant.GetConversations,
        params: {
          page: pageParam,
          limit: queryArg.limit,
          search: queryArg.search,
        },
      }),
      providesTags: ["GetAllConversations"],
    }),
    getQueries: builder.infiniteQuery<
      IApiPaginatedResponse<TQueriesHistory[]>,
      { limit?: number; search: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { page, lastPage } = resp.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ pageParam, queryArg }) => ({
        url: APIEndpoints.MySpace.AIAssistant.GetConversationsWithQuestions,
        params: {
          page: pageParam,
          limit: queryArg.limit,
          search: queryArg.search,
        },
      }),
      providesTags: ["GetAllQueries"],
    }),
    getQuestionsBySessionId: builder.infiniteQuery<
      IApiPaginatedResponse<IQuerySingleQuestion[]>,
      { sessionId?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { page, lastPage } = resp.meta;
          return page < lastPage ? page + 1 : undefined;
        },
      },
      query: ({ pageParam, queryArg }) => ({
        url: APIEndpoints.MySpace.AIAssistant.GetQuestionsBySessionId,
        params: {
          page: pageParam,
          sessionId: queryArg.sessionId,
        },
      }),
    }),
    pinConversation: builder.mutation<IApiResponse<void>, { sessionId?: string; pinned: boolean }>({
      query: (body) => ({
        url: APIEndpoints.MySpace.AIAssistant.PinConversation,
        method: "POST",
        body,
      }),
      invalidatesTags: ["GetAllConversations"],
    }),
    renameConversation: builder.mutation<IApiResponse<void>, { sessionId?: string; newTitle: string }>({
      query: (body) => ({
        url: APIEndpoints.MySpace.AIAssistant.RenameConversation,
        method: "POST",
        body,
      }),
      invalidatesTags: ["GetAllConversations"],
    }),
    editMessage: builder.mutation<IApiResponse<IChatSessionData>, { messageId: number; content: string }>({
      query: (body) => ({
        url: APIEndpoints.MySpace.AIAssistant.EditMessage,
        method: "PATCH",
        body,
      }),
    }),
    deleteConversation: builder.mutation<IApiResponse<void>, { conversationIds: number[] }>({
      query: (body) => ({
        url: APIEndpoints.MySpace.AIAssistant.DeleteConversation,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["GetAllConversations"],
    }),
    speechToText: builder.mutation<IApiResponse<{ output: string }>, FormData>({
      query: (body) => ({
        url: APIEndpoints.MySpace.AIAssistant.SpeechToText,
        method: "POST",
        body,
      }),
    }),
    getShareLink: builder.query<
      IApiResponse<{
        token: string;
        url: string;
        isActive: boolean;
        expiresAt: string | null;
        snapshotMessageId: number | null;
        sessionId: string;
        viewCount: number;
      } | null>,
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: APIEndpoints.MySpace.AIAssistant.ShareConversation(sessionId),
      }),
      providesTags: (_r, _e, arg) => [{ type: "AIShareLink", id: arg.sessionId }],
    }),
    createShareLink: builder.mutation<
      IApiResponse<{
        token: string;
        url: string;
        isActive: boolean;
        expiresAt: string | null;
        snapshotMessageId: number | null;
        sessionId: string;
      }>,
      { sessionId: string; expiresInDays?: number }
    >({
      query: ({ sessionId, expiresInDays }) => ({
        url: APIEndpoints.MySpace.AIAssistant.ShareConversation(sessionId),
        method: "POST",
        body: expiresInDays != null ? { expiresInDays } : {},
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AIShareLink", id: arg.sessionId }],
    }),
    revokeShareLink: builder.mutation<IApiResponse<null>, { sessionId: string }>({
      query: ({ sessionId }) => ({
        url: APIEndpoints.MySpace.AIAssistant.ShareConversation(sessionId),
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "AIShareLink", id: arg.sessionId }],
    }),
  }),
});

export const {
  useAiSmartChatMutation,
  useGetConversationHistoryInfiniteQuery,
  useGetQueriesInfiniteQuery,
  usePinConversationMutation,
  useRenameConversationMutation,
  useDeleteConversationMutation,
  useLazyGetSingleConversationQuery,
  useEditMessageMutation,
  useGetQuestionsBySessionIdInfiniteQuery,
  useSpeechToTextMutation,
  useGetShareLinkQuery,
  useLazyGetShareLinkQuery,
  useCreateShareLinkMutation,
  useRevokeShareLinkMutation,
} = aiAssistantApi;
